import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeIcon, ClipboardListIcon, CalendarIcon, UserIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PlusCircleIcon, EyeIcon, EditIcon, TrashIcon, CrownIcon, BuildingIcon, UploadIcon, AlertCircleIcon } from 'lucide-react';
import { mockListings, mockVisits, currentUser, formatCurrency, licensePlans, getDaysRemaining, isLicenseExpired } from '../utils/mockData';
import LicenseStatus from '../components/LicenseStatus';
const UserDashboardPage = () => {
  const [activeTab, setActiveTab] = useState<'listings' | 'visits' | 'profile' | 'license'>('listings');
  const [companyLogo, setCompanyLogo] = useState(currentUser.companyLogo || '');
  const userListings = mockListings.filter(listing => listing.owner.id === currentUser.id);
  const userListingIds = userListings.map(listing => listing.id);
  const userVisits = mockVisits.filter(visit => userListingIds.includes(visit.listingId));
  const handleVisitStatusChange = (visitId: string, status: 'accepted' | 'rejected') => {
    console.log(`Visit ${visitId} ${status}`);
  };
  const currentPlan = currentUser.license ? licensePlans.find(p => p.id === currentUser.license.licenseId) : null;
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const logoUrl = URL.createObjectURL(file);
      setCompanyLogo(logoUrl);
      // In a real app, you'd upload this to your server
    }
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-1146">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1147">
        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-1148">My Dashboard</h1>
        <div className="flex flex-col md:flex-row gap-8" data-id="element-1149">
          <div className="md:w-64 shrink-0" data-id="element-1150">
            <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1151">
              <div className="p-6 border-b border-gray-200" data-id="element-1152">
                <div className="flex items-center" data-id="element-1153">
                  {currentUser.companyLogo ? <img src={currentUser.companyLogo} alt={currentUser.companyName} className="h-12 w-12 rounded-full object-cover" data-id="element-1154" /> : <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600" data-id="element-1155">
                      <UserIcon className="h-6 w-6" data-id="element-1156" />
                    </div>}
                  <div className="ml-3" data-id="element-1157">
                    <p className="font-medium text-gray-900" data-id="element-1158">
                      {currentUser.companyName || currentUser.name}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-1159">{currentUser.email}</p>
                  </div>
                </div>
              </div>
              <nav className="p-2" data-id="element-1160">
                <button onClick={() => setActiveTab('listings')} className={`w-full flex items-center px-4 py-3 rounded-md text-left ${activeTab === 'listings' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1161">
                  <HomeIcon className="h-5 w-5 mr-3" data-id="element-1162" />
                  My Listings
                </button>
                <button onClick={() => setActiveTab('visits')} className={`w-full flex items-center px-4 py-3 rounded-md text-left ${activeTab === 'visits' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1163">
                  <CalendarIcon className="h-5 w-5 mr-3" data-id="element-1164" />
                  Visit Requests
                  {userVisits.filter(v => v.status === 'pending').length > 0 && <span className="ml-auto bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-id="element-1165">
                      {userVisits.filter(v => v.status === 'pending').length}
                    </span>}
                </button>
                <button onClick={() => setActiveTab('license')} className={`w-full flex items-center px-4 py-3 rounded-md text-left ${activeTab === 'license' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1166">
                  <CrownIcon className="h-5 w-5 mr-3" data-id="element-1167" />
                  License & Plans
                </button>
                <button onClick={() => setActiveTab('profile')} className={`w-full flex items-center px-4 py-3 rounded-md text-left ${activeTab === 'profile' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1168">
                  <UserIcon className="h-5 w-5 mr-3" data-id="element-1169" />
                  My Profile
                </button>
              </nav>
              <div className="p-4 mt-2" data-id="element-1170">
                <Link to="/create-listing" className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md transition duration-200" data-id="element-1171">
                  <PlusCircleIcon className="h-5 w-5 mr-2" data-id="element-1172" />
                  Add New Listing
                </Link>
              </div>
            </div>
          </div>
          <div className="flex-1" data-id="element-1173">
            {activeTab === 'listings' && <div className="space-y-6" data-id="element-1174">
                <LicenseStatus data-id="element-1175" />
                <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1176">
                  <div className="flex items-center justify-between mb-6" data-id="element-1177">
                    <h2 className="text-xl font-semibold text-gray-900" data-id="element-1178">
                      My Listings
                    </h2>
                    <Link to="/create-listing" className="flex items-center text-sm bg-primary hover:bg-[rgb(var(--color-primary-dark))] text-white font-medium px-3 py-1.5 rounded-md transition duration-200" data-id="element-1179">
                      <PlusCircleIcon className="h-4 w-4 mr-1" data-id="element-1180" />
                      New Listing
                    </Link>
                  </div>
                  {userListings.length === 0 ? <div className="text-center py-12" data-id="element-1181">
                      <ClipboardListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-1182" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1183">
                        No listings yet
                      </h3>
                      <p className="text-gray-600 mb-4" data-id="element-1184">
                        You haven't created any property listings yet.
                      </p>
                      <Link to="/create-listing" className="inline-block bg-primary hover:bg-[rgb(var(--color-primary-dark))] text-white font-medium px-4 py-2 rounded-md transition duration-200" data-id="element-1185">
                        Create Your First Listing
                      </Link>
                    </div> : <div className="overflow-x-auto" data-id="element-1186">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-1187">
                        <thead data-id="element-1188">
                          <tr data-id="element-1189">
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1190">
                              Property
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1191">
                              Price
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1192">
                              Type
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1193">
                              Status
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1194">
                              Visits
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1195">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1196">
                          {userListings.map(listing => {
                      const listingVisits = userVisits.filter(v => v.listingId === listing.id);
                      return <tr key={listing.id} className="hover:bg-gray-50" data-id="element-1197">
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1198">
                                  <div className="flex items-center" data-id="element-1199">
                                    <div className="h-10 w-10 flex-shrink-0" data-id="element-1200">
                                      <img className="h-10 w-10 rounded-md object-cover" src={listing.images[0]} alt={listing.title} data-id="element-1201" />
                                    </div>
                                    <div className="ml-4" data-id="element-1202">
                                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs" data-id="element-1203">
                                        {listing.title}
                                      </div>
                                      <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-1204">
                                        {listing.location}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1205">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-1206">
                                    {formatCurrency(listing.price)}
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1207">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${listing.type === 'house' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-1208">
                                    {listing.type}
                                  </span>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1209">
                                  {listing.status === 'approved' && <span className="flex items-center text-xs font-medium text-green-800 bg-green-100 px-2 py-1 rounded-full" data-id="element-1210">
                                      <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-1211" />
                                      Approved
                                    </span>}
                                  {listing.status === 'pending' && <span className="flex items-center text-xs font-medium text-yellow-800 bg-yellow-100 px-2 py-1 rounded-full" data-id="element-1212">
                                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-1213" />
                                      Under Review
                                    </span>}
                                  {listing.status === 'rejected' && <span className="flex items-center text-xs font-medium text-red-800 bg-red-100 px-2 py-1 rounded-full" data-id="element-1214">
                                      <XCircleIcon className="h-3 w-3 mr-1" data-id="element-1215" />
                                      Rejected
                                    </span>}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1216">
                                  <div className="text-sm text-gray-900" data-id="element-1217">
                                    {listingVisits.length} total
                                    {listingVisits.filter(v => v.status === 'pending').length > 0 && <span className="ml-1 text-xs bg-yellow-100 text-yellow-800 px-1.5 rounded-full" data-id="element-1218">
                                        {listingVisits.filter(v => v.status === 'pending').length}{' '}
                                        pending
                                      </span>}
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1219">
                                  <div className="flex justify-end space-x-2" data-id="element-1220">
                                    {listing.status === 'approved' && <Link to={`/listing/${listing.id}`} className="text-blue-600 hover:text-blue-900" title="View" data-id="element-1221">
                                        <EyeIcon className="h-5 w-5" data-id="element-1222" />
                                      </Link>}
                                    {listing.status === 'rejected' && <button className="text-yellow-600 hover:text-yellow-900" title="View rejection reason" onClick={() => alert(listing.rejectionReason || 'No reason provided')} data-id="element-1223">
                                        <AlertCircleIcon className="h-5 w-5" data-id="element-1224" />
                                      </button>}
                                    <Link to={`/edit-listing/${listing.id}`} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-1225">
                                      <EditIcon className="h-5 w-5" data-id="element-1226" />
                                    </Link>
                                    <button className="text-red-600 hover:text-red-900" onClick={() => console.log('Delete listing', listing.id)} title="Delete" data-id="element-1227">
                                      <TrashIcon className="h-5 w-5" data-id="element-1228" />
                                    </button>
                                  </div>
                                </td>
                              </tr>;
                    })}
                        </tbody>
                      </table>
                    </div>}
                </div>
              </div>}
            {activeTab === 'visits' && <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1229">
                <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-1230">
                  Visit Requests
                </h2>
                {userVisits.length === 0 ? <div className="text-center py-12" data-id="element-1231">
                    <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-1232" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1233">
                      No visit requests
                    </h3>
                    <p className="text-gray-600" data-id="element-1234">
                      You don't have any visit requests for your properties yet.
                    </p>
                  </div> : <div className="space-y-6" data-id="element-1235">
                    {userVisits.map(visit => {
                const listing = mockListings.find(l => l.id === visit.listingId);
                return <div key={visit.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1236">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4" data-id="element-1237">
                            <div className="flex items-center mb-2 md:mb-0" data-id="element-1238">
                              <div className="h-10 w-10 flex-shrink-0" data-id="element-1239">
                                <img className="h-10 w-10 rounded-md object-cover" src={listing?.images[0]} alt={listing?.title} data-id="element-1240" />
                              </div>
                              <div className="ml-4" data-id="element-1241">
                                <div className="text-sm font-medium text-gray-900" data-id="element-1242">
                                  {listing?.title}
                                </div>
                                <div className="text-sm text-gray-500" data-id="element-1243">
                                  {listing?.location}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center" data-id="element-1244">
                              {visit.status === 'pending' && <span className="flex items-center text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full text-xs font-medium" data-id="element-1245">
                                  <ClockIcon className="h-3 w-3 mr-1" data-id="element-1246" />
                                  Pending
                                </span>}
                              {visit.status === 'accepted' && <span className="flex items-center text-green-600 bg-green-100 px-2 py-1 rounded-full text-xs font-medium" data-id="element-1247">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-1248" />
                                  Accepted
                                </span>}
                              {visit.status === 'rejected' && <span className="flex items-center text-red-600 bg-red-100 px-2 py-1 rounded-full text-xs font-medium" data-id="element-1249">
                                  <XCircleIcon className="h-3 w-3 mr-1" data-id="element-1250" />
                                  Declined
                                </span>}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-1251">
                            <div data-id="element-1252">
                              <p className="text-xs text-gray-500" data-id="element-1253">Visitor</p>
                              <p className="text-sm font-medium" data-id="element-1254">
                                {visit.userName}
                              </p>
                              <p className="text-sm text-gray-600" data-id="element-1255">
                                {visit.userEmail}
                              </p>
                              <p className="text-sm text-gray-600" data-id="element-1256">
                                {visit.userPhone}
                              </p>
                            </div>
                            <div data-id="element-1257">
                              <p className="text-xs text-gray-500" data-id="element-1258">
                                Date & Time
                              </p>
                              <p className="text-sm font-medium" data-id="element-1259">
                                {visit.date}
                              </p>
                              <p className="text-sm text-gray-600" data-id="element-1260">
                                {visit.time}
                              </p>
                            </div>
                            <div data-id="element-1261">
                              <p className="text-xs text-gray-500" data-id="element-1262">Message</p>
                              <p className="text-sm text-gray-600" data-id="element-1263">
                                {visit.message || 'No message provided'}
                              </p>
                            </div>
                          </div>
                          {visit.status === 'pending' && <div className="flex justify-end space-x-3" data-id="element-1264">
                              <button onClick={() => handleVisitStatusChange(visit.id, 'rejected')} className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-1265">
                                Decline
                              </button>
                              <button onClick={() => handleVisitStatusChange(visit.id, 'accepted')} className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700" data-id="element-1266">
                                Accept
                              </button>
                            </div>}
                        </div>;
              })}
                  </div>}
              </div>}
            {activeTab === 'license' && <div className="space-y-6" data-id="element-1267">
                <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1268">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-1269">
                    License & Subscription
                  </h2>
                  {currentUser.license ? <div className="space-y-6" data-id="element-1270">
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6" data-id="element-1271">
                        <div className="flex items-start justify-between mb-4" data-id="element-1272">
                          <div data-id="element-1273">
                            <p className="text-sm text-blue-700 mb-1" data-id="element-1274">
                              Current Plan
                            </p>
                            <h3 className="text-2xl font-bold text-blue-900 capitalize" data-id="element-1275">
                              {currentPlan?.planName}
                            </h3>
                          </div>
                          <CrownIcon className="h-8 w-8 text-blue-600" data-id="element-1276" />
                        </div>
                        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1277">
                          <div data-id="element-1278">
                            <p className="text-sm text-blue-700" data-id="element-1279">
                              Listings Used
                            </p>
                            <p className="text-xl font-bold text-blue-900" data-id="element-1280">
                              {currentUser.license.usedListings} /{' '}
                              {currentUser.license.maxListings}
                            </p>
                          </div>
                          <div data-id="element-1281">
                            <p className="text-sm text-blue-700" data-id="element-1282">
                              Days Remaining
                            </p>
                            <p className="text-xl font-bold text-blue-900" data-id="element-1283">
                              {getDaysRemaining(currentUser.license.expiryDate)}
                            </p>
                          </div>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-3 mb-4" data-id="element-1284">
                          <div className="bg-blue-600 h-3 rounded-full transition-all duration-300" style={{
                      width: `${currentUser.license.usedListings / currentUser.license.maxListings * 100}%`
                    }} data-id="element-1285" />
                        </div>
                        <p className="text-sm text-blue-700 mb-4" data-id="element-1286">
                          Expires on{' '}
                          {new Date(currentUser.license.expiryDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                        </p>
                        <Link to="/license-plans" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-md transition duration-200" data-id="element-1287">
                          Upgrade Plan
                        </Link>
                      </div>
                      <div data-id="element-1288">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1289">
                          Plan Features
                        </h3>
                        <ul className="space-y-2" data-id="element-1290">
                          {currentPlan?.features.map((feature, index) => <li key={index} className="flex items-center text-gray-700" data-id="element-1291">
                              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-1292" />
                              {feature}
                            </li>)}
                        </ul>
                      </div>
                    </div> : <div className="text-center py-8" data-id="element-1293">
                      <CrownIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" data-id="element-1294" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1295">
                        No Active License
                      </h3>
                      <p className="text-gray-600 mb-6" data-id="element-1296">
                        Purchase a license to start listing your properties.
                      </p>
                      <Link to="/license-plans" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-md transition duration-200" data-id="element-1297">
                        View License Plans
                      </Link>
                    </div>}
                </div>
              </div>}
            {activeTab === 'profile' && <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1298">
                <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-1299">
                  {currentUser.companyName ? 'Agency Profile' : 'My Profile'}
                </h2>
                <div className="max-w-2xl mx-auto" data-id="element-1300">
                  <div className="mb-8" data-id="element-1301">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1302">
                      Company Logo
                    </label>
                    <div className="flex items-center space-x-4" data-id="element-1303">
                      <div className="h-24 w-24 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden" data-id="element-1304">
                        {companyLogo ? <img src={companyLogo} alt="Company logo" className="h-full w-full object-cover" data-id="element-1305" /> : <BuildingIcon className="h-12 w-12 text-gray-400" data-id="element-1306" />}
                      </div>
                      <label className="cursor-pointer bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium px-4 py-2 rounded-md transition duration-200" data-id="element-1307">
                        <UploadIcon className="h-4 w-4 inline mr-2" data-id="element-1308" />
                        Upload Logo
                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-id="element-1309" />
                      </label>
                    </div>
                    <p className="mt-2 text-sm text-gray-500" data-id="element-1310">
                      Recommended: Square image, at least 200x200px
                    </p>
                  </div>
                  <form className="space-y-6" data-id="element-1311">
                    <div className="border-b border-gray-200 pb-6" data-id="element-1312">
                      <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1313">
                        Company Information
                      </h3>
                      <div className="space-y-4" data-id="element-1314">
                        <div data-id="element-1315">
                          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1316">
                            Company Name
                          </label>
                          <input type="text" id="companyName" name="companyName" defaultValue={currentUser.companyName} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Your Real Estate Agency" data-id="element-1317" />
                        </div>
                        <div data-id="element-1318">
                          <label htmlFor="companyNIF" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1319">
                            Company NIF (Tax ID)
                          </label>
                          <input type="text" id="companyNIF" name="companyNIF" defaultValue={currentUser.companyNIF} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="123456789" data-id="element-1320" />
                          <p className="mt-1 text-sm text-gray-500" data-id="element-1321">
                            Your company tax identification number
                          </p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-1322">
                      <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1323">
                        Contact Information
                      </h3>
                      <div className="space-y-4" data-id="element-1324">
                        <div data-id="element-1325">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1326">
                            Contact Person Name
                          </label>
                          <input type="text" id="name" name="name" defaultValue={currentUser.name} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1327" />
                        </div>
                        <div data-id="element-1328">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1329">
                            Email Address
                          </label>
                          <input type="email" id="email" name="email" defaultValue={currentUser.email} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1330" />
                        </div>
                        <div data-id="element-1331">
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1332">
                            Phone Number
                          </label>
                          <input type="tel" id="phone" name="phone" defaultValue={currentUser.phone} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1333" />
                        </div>
                      </div>
                    </div>
                    <div className="pt-4" data-id="element-1334">
                      <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200" data-id="element-1335">
                        Update Profile
                      </button>
                    </div>
                  </form>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default UserDashboardPage;